import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UploadCloud, Search, Sparkles, Trash2, ExternalLink, FileSpreadsheet, Scissors, RotateCcw, CloudUpload, BarChart3 } from '../lib/icons';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  deposerDocument,
  fetchDocuments,
  rechercherDocuments,
  analyserLot,
  mettreALaCorbeille,
  restaurerDocument,
  fileMiroir,
  migrerArchives,
  urlDocument,
  analyseProfonde,
  fetchKpisCampagnes,
  majKpiCampagne,
  supprimerKpiCampagne,
  SOURCES,
  LIENS,
} from '../lib/docs';
import { useAuth } from '../lib/auth';
import { fmt, PageTitle } from '../components/shared/badges';

const th = 'px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500';
const td = 'px-3 py-2 align-top';
const ONGLETS = [
  { id: 'tous', label: 'Tous les documents', sous: 'Tout l’ERP, centralisé' },
  { id: 'coffre', label: 'Dépôts libres', sous: 'Administratif divers' },
  { id: 'corbeille', label: 'Corbeille (30 j)', sous: 'Suppression réversible' },
];

export default function Coffre() {
  const { user } = useAuth();
  const [onglet, setOnglet] = useState('tous');
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [miroir, setMiroir] = useState({ enAttente: 0, configure: false, identifiants: false });
  const [filtres, setFiltres] = useState({ categorie: '', annee: '', source: '' });
  const [question, setQuestion] = useState('');
  const [recherche, setRecherche] = useState(null);
  const [enCours, setEnCours] = useState(null);
  const [lot, setLot] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [survol, setSurvol] = useState(false);
  const input = useRef(null);
  const inputLot = useRef(null);

  const load = useCallback(async () => {
    const data = await fetchDocuments({ corbeille: onglet === 'corbeille', source: onglet === 'coffre' ? 'coffre' : '' });
    setDocs(data.documents ?? []);
    setCategories(data.categories ?? []);
    setMiroir(await fileMiroir());
    setKpis(await fetchKpisCampagnes());
  }, [onglet]);
  useEffect(() => { load(); setRecherche(null); }, [load]);

  // État Drive affiché TEL QU'IL EST VÉRIFIÉ : des identifiants présents ne valent
  // pas une copie vérifiée, et une copie vérifiée est datée par le serveur.
  const etatDrive = useMemo(() => {
    const jour = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : null);
    const compte = miroir.compte ? `compte ${miroir.compte}` : '';
    if (miroir.erreurEtat) return { ton: 'alerte', titre: 'État du miroir Drive indisponible', detail: miroir.erreurEtat };
    if (!miroir.identifiants) return { ton: 'absent', titre: 'Identifiants Google Drive absents', detail: 'aucune copie possible' };
    if (!miroir.dossierConfigure) return { ton: 'absent', titre: 'Dossier Drive non renseigné', detail: compte };
    if (miroir.reconnexionRequise)
      return { ton: 'alerte', titre: 'Autorisation Google à renouveler', detail: miroir.derniereErreur || compte };
    if (miroir.ecritureVerifieeLe)
      return { ton: 'verifie', titre: `Copie Drive vérifiée le ${jour(miroir.ecritureVerifieeLe)}`, detail: compte };
    if (miroir.dernierExportReussi)
      return { ton: 'attente', titre: `Dernière copie réussie le ${jour(miroir.dernierExportReussi)}`, detail: `${compte} · écriture non revérifiée depuis` };
    if (miroir.lectureVerifieeLe)
      return {
        ton: 'attente',
        titre: 'Identifiants et dossier configurés — copie pas encore vérifiée',
        detail: `lecture du dossier vérifiée le ${jour(miroir.lectureVerifieeLe)}${compte ? ` · ${compte}` : ''}`,
      };
    return { ton: 'attente', titre: 'Identifiants configurés — connexion jamais vérifiée', detail: compte };
  }, [miroir]);

  // Lecture profonde : extraction des KPI métier du document vers l'historique des campagnes.
  const deepScan = async (doc) => {
    setEnCours(`Lecture profonde de ${doc.nom} par l’IA…`);
    const { error, exploitable, motif, kpi } = await analyseProfonde(doc.id, user?.name);
    setEnCours(null);
    if (error) return toast.error(error);
    if (!exploitable) return toast.warning(motif, { duration: 9000 });
    toast.success(
      `Campagne ${kpi.campagne} extraite — CA ${fmt(kpi.chiffreAffaires, 3)} TND · ${fmt(kpi.volumeHuileKg, 1)} kg d’huile · rendement ${fmt(kpi.rendementMoyen, 2)} %`,
      { duration: 10000 }
    );
    load();
  };

  const deposer = async (files) => {
    const liste = Array.from(files ?? []);
    if (!liste.length) return;
    for (const [i, file] of liste.entries()) {
      setEnCours(`${i + 1}/${liste.length} — ${file.name} : indexation IA…`);
      const { error, document: doc, doublon } = await deposerDocument(file, { source: 'coffre', deposePar: user?.name });
      if (error) {
        toast.error(error);
        continue;
      }
      if (doublon) {
        toast.warning(
          `${file.name} déjà présent (${doublon.motif === 'empreinte' ? 'fichier identique' : 'même pièce, montant et tiers'}) — aucune écriture créée : ${doc.nom}`,
          { duration: 9000 }
        );
        continue;
      }
      toast.success(`${doc.nom} archivé — ${doc.categorie}${doc.index?.tiers ? ` · ${doc.index.tiers}` : ''}`);
    }
    setEnCours(null);
    load();
  };

  const decouper = async (file) => {
    if (!file) return;
    setEnCours(`Analyse du lot ${file.name} par l’IA…`);
    const { error, segments, pages } = await analyserLot(file, { deposePar: user?.name });
    setEnCours(null);
    if (error) return toast.error(error);
    setLot({ file, pages, segments });
  };

  const appliquerLot = async () => {
    setEnCours('Découpage et archivage des documents…');
    const { error, documents, doublons } = await analyserLot(lot.file, { appliquer: true, deposePar: user?.name });
    setEnCours(null);
    if (error) return toast.error(error);
    toast.success(`${documents.length} document(s) archivé(s)${doublons.length ? ` · ${doublons.length} doublon(s) ignoré(s)` : ''}`);
    setLot(null);
    load();
  };

  const chercher = async (e) => {
    e.preventDefault();
    if (!question.trim()) return setRecherche(null);
    setEnCours('Recherche en langage naturel…');
    const { error, documents, mode, explication } = await rechercherDocuments(question);
    setEnCours(null);
    if (error) return toast.error(error);
    setRecherche({ documents, mode, explication });
  };

  const visibles = useMemo(() => {
    const base = recherche ? recherche.documents : docs;
    return base.filter(
      (d) =>
        (!filtres.categorie || d.categorie === filtres.categorie) &&
        (!filtres.annee || d.annee === filtres.annee) &&
        (!filtres.source || d.source === filtres.source)
    );
  }, [docs, recherche, filtres]);

  const annees = useMemo(() => [...new Set(docs.map((d) => d.annee))].sort().reverse(), [docs]);
  const poids = useMemo(() => docs.reduce((s, d) => s + (d.taille || 0), 0) / (1024 * 1024), [docs]);

  return (
    <div data-testid="page-coffre" className="space-y-6">
      <PageTitle
        emoji="🗄️"
        titre="Centre Documentaire IA"
        sous="Coffre-fort de l’huilerie : tous les fichiers de l’ERP, indexés par l’IA, anti-doublon et sauvegardés."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        {[
          { k: 'total', label: 'Documents archivés', valeur: docs.length, sous: `${poids.toFixed(1)} Mo` },
          { k: 'categories', label: 'Catégories utilisées', valeur: new Set(docs.map((d) => d.categorie)).size, sous: `${annees.length} exercice(s)` },
          { k: 'indexes', label: 'Indexés par l’IA', valeur: docs.filter((d) => (d.index?.motsCles ?? []).length > 0).length, sous: 'recherche naturelle' },
          {
            k: 'miroir',
            label: 'Miroir externe',
            valeur: miroir.enAttente,
            sous: etatDrive.titre,
          },
        ].map((c) => (
          <div key={c.k} data-testid={`kpi-${c.k}`} className="border border-slate-200 border-l-2 border-l-slate-900 bg-white px-4 py-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{c.label}</p>
            <p className="tabular font-data text-2xl font-medium text-slate-900">{c.valeur}</p>
            <p className="text-[11px] text-slate-500">{c.sous}</p>
          </div>
        ))}
      </div>

      <form onSubmit={chercher} className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="coffre-recherche"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Recherche en langage naturel : « la police d’assurance du camion », « factures STEG de janvier »…"
            className="h-12 w-full border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-900 outline-none focus:border-slate-900"
          />
        </div>
        <button type="submit" data-testid="coffre-chercher" className="flex h-12 items-center justify-center gap-2 bg-slate-900 px-5 font-heading text-sm font-bold text-white hover:bg-slate-700">
          <Sparkles size={16} /> Chercher
        </button>
        {recherche && (
          <button type="button" data-testid="coffre-reset-recherche" onClick={() => { setQuestion(''); setRecherche(null); }} className="h-12 border border-slate-300 px-4 text-sm font-bold text-slate-700 hover:bg-slate-100">
            Effacer
          </button>
        )}
      </form>

      {recherche && (
        <p data-testid="coffre-explication" className="border-l-4 border-violet-600 bg-violet-50 px-3 py-2 text-xs text-slate-700">
          {recherche.documents.length} résultat(s) · {recherche.mode === 'ia' ? 'analyse IA' : 'repli mots-clés'}
          {recherche.explication ? ` — ${recherche.explication}` : ''}
        </p>
      )}

      <div className="flex flex-wrap gap-px border border-slate-200 bg-slate-200">
        {ONGLETS.map((o) => (
          <button
            key={o.id}
            type="button"
            data-testid={`onglet-${o.id}`}
            onClick={() => setOnglet(o.id)}
            className={`flex-1 px-4 py-3 text-left transition-colors duration-150 ${onglet === o.id ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`}
          >
            <p className="font-heading text-sm font-bold">{o.label}</p>
            <p className={`text-xs ${onglet === o.id ? 'text-slate-300' : 'text-slate-500'}`}>{o.sous}</p>
          </button>
        ))}
      </div>

      {onglet !== 'corbeille' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
          <div
            data-testid="coffre-depot"
            onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
            onDragLeave={() => setSurvol(false)}
            onDrop={(e) => { e.preventDefault(); setSurvol(false); deposer(e.dataTransfer.files); }}
            onClick={() => !enCours && input.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 border-2 border-dashed px-6 py-10 text-center transition-colors duration-150 ${
              enCours ? 'border-violet-500 bg-violet-50' : survol ? 'border-slate-900 bg-slate-50' : 'border-slate-300 bg-white hover:bg-slate-50'
            }`}
          >
            {enCours ? <Sparkles size={28} className="animate-pulse text-violet-700" /> : <UploadCloud size={28} className="text-slate-500" />}
            <p className="font-heading text-sm font-bold text-slate-900">
              {enCours ?? 'Glissez-déposez vos documents administratifs (PDF, images, tableaux)'}
            </p>
            <p className="text-xs text-slate-500">
              Contrats, assurances, agréments, courriers, attestations… L’IA lit, catégorise et indexe. Les doublons sont bloqués automatiquement.
            </p>
            <input
              ref={input}
              data-testid="coffre-input"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.csv,.xlsx,.txt"
              className="hidden"
              disabled={Boolean(enCours)}
              onChange={(e) => deposer(e.target.files)}
            />
          </div>

          <div className="border border-slate-200 border-l-2 border-l-violet-600 bg-white px-4 py-4">
            <p className="flex items-center gap-2 font-heading text-sm font-bold text-slate-900">
              <Scissors size={15} className="text-violet-700" /> Lot scanné multi-documents
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Un PDF CamScanner mélangeant factures, reçus et courriers ? L’IA détecte où commence et s’arrête chaque document et les classe séparément.
            </p>
            <button
              type="button"
              data-testid="coffre-decouper"
              disabled={Boolean(enCours)}
              onClick={() => inputLot.current?.click()}
              className="mt-3 flex h-11 w-full items-center justify-center gap-2 border-2 border-violet-700 bg-violet-50 font-heading text-sm font-bold text-violet-800 hover:bg-violet-700 hover:text-white disabled:opacity-50"
            >
              <Scissors size={15} /> Découper un lot PDF
            </button>
            <input ref={inputLot} data-testid="coffre-input-lot" type="file" accept=".pdf" className="hidden" onChange={(e) => decouper(e.target.files?.[0])} />
            <button
              type="button"
              data-testid="coffre-migrer"
              disabled={Boolean(enCours)}
              onClick={async () => {
                setEnCours('Migration des justificatifs déjà en base…');
                const { error, migres } = await migrerArchives();
                setEnCours(null);
                if (error) return toast.error(error);
                toast.success(migres ? `${migres} justificatif(s) migré(s) vers le stockage objet` : 'Aucun justificatif à migrer — tout est déjà archivé');
                load();
              }}
              className="mt-2 h-10 w-full border border-slate-300 text-xs font-bold text-slate-600 hover:bg-slate-900 hover:text-white disabled:opacity-50"
            >
              Migrer les anciens justificatifs de la base
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="filtre-categorie" value={filtres.categorie} onChange={(e) => setFiltres({ ...filtres, categorie: e.target.value })} className="h-10 border border-slate-300 bg-white px-2 text-sm">
          <option value="">Toutes les catégories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="filtre-annee" value={filtres.annee} onChange={(e) => setFiltres({ ...filtres, annee: e.target.value })} className="h-10 border border-slate-300 bg-white px-2 text-sm">
          <option value="">Toutes les années</option>
          {annees.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select data-testid="filtre-source" value={filtres.source} onChange={(e) => setFiltres({ ...filtres, source: e.target.value })} className="h-10 border border-slate-300 bg-white px-2 text-sm">
          <option value="">Toutes les provenances</option>
          {Object.entries(SOURCES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {etatDrive.ton !== 'verifie' || miroir.enAttente > 0 ? (
          <p
            data-testid="miroir-bandeau"
            className={`ml-auto flex items-center gap-2 border px-3 py-2 text-xs ${
              etatDrive.ton === 'verifie'
                ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                : etatDrive.ton === 'alerte'
                  ? 'border-red-300 bg-red-50 text-red-900'
                  : 'border-amber-300 bg-amber-50 text-amber-900'
            }`}
          >
            <CloudUpload size={14} />
            <span>
              <span data-testid="miroir-etat" className="font-semibold">{etatDrive.titre}</span>
              {etatDrive.detail ? <span className="text-[11px]"> — {etatDrive.detail}</span> : null}
              <span className="text-[11px]">
                {' '}· {miroir.enAttente} fichier(s) en attente de copie
                {miroir.envoyes ? `, ${miroir.envoyes} déjà copié(s)` : ''}
              </span>
            </span>
          </p>
        ) : null}
      </div>

      <section className="border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="table-documents">
            <thead className="bg-slate-50">
              <tr>{['Document', 'Catégorie', 'Provenance', 'Tiers', 'Date', 'Montant', 'Mots-clés', 'Déposé par', ''].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibles.length === 0 ? (
                <tr><td colSpan={9} data-testid="documents-vide" className="px-3 py-10 text-center text-sm text-slate-500">
                  {onglet === 'corbeille' ? 'Corbeille vide.' : 'Aucun document — déposez vos premiers fichiers ci-dessus.'}
                </td></tr>
              ) : (
                visibles.map((d) => {
                  const src = SOURCES[d.source] ?? { label: d.source, couleur: 'bg-slate-100 text-slate-700 border-slate-300' };
                  return (
                    <tr key={d.id} data-testid={`doc-${d.id}`} className="hover:bg-slate-50">
                      <td className={td}>
                        <a href={urlDocument(d.id)} target="_blank" rel="noreferrer" data-testid={`ouvrir-${d.id}`} className="flex items-start gap-2 text-sm font-bold text-slate-900 hover:underline">
                          <FileSpreadsheet size={14} className="mt-0.5 shrink-0 text-slate-400" />
                          <span>
                            {d.nom}
                            {d.index?.resume && <span className="block font-normal text-xs text-slate-500">{d.index.resume}</span>}
                          </span>
                        </a>
                      </td>
                      <td className={`${td} text-xs text-slate-700`}>{d.categorie}</td>
                      <td className={td}>
                        <span className={`inline-block border px-1.5 py-0.5 font-data text-[10px] font-bold uppercase ${src.couleur}`}>{src.label}</span>
                      </td>
                      <td className={`${td} text-xs text-slate-700`}>{d.index?.tiers || '—'}</td>
                      <td className={`${td} font-data text-xs text-slate-600`}>{d.index?.date || `${d.annee}-${d.mois}`}</td>
                      <td className={`${td} tabular text-right font-data text-xs`}>{d.index?.montant ? fmt(d.index.montant, 3) : '—'}</td>
                      <td className={td}>
                        <div className="flex max-w-[240px] flex-wrap gap-1">
                          {(d.index?.motsCles ?? []).slice(0, 6).map((m) => (
                            <span key={m} className="border border-slate-200 bg-slate-50 px-1 py-0.5 font-data text-[10px] text-slate-600">{m}</span>
                          ))}
                        </div>
                      </td>
                      <td className={`${td} text-xs text-slate-600`}>{d.deposePar || '—'}<span className="block font-data text-[10px] text-slate-400">{(d.le || '').slice(0, 16).replace('T', ' ')}</span></td>
                      <td className={`${td} text-right`}>
                        <div className="flex justify-end gap-1.5">
                          {LIENS[d.source] && (
                            <Link to={LIENS[d.source]} data-testid={`ecriture-${d.id}`} title="Voir l’écriture d’origine" className="border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-900 hover:text-white">
                              <ExternalLink size={13} />
                            </Link>
                          )}
                          {onglet !== 'corbeille' && (
                            <button
                              type="button"
                              data-testid={`deepscan-${d.id}`}
                              title="Lancer l’analyse profonde : extraire les KPI de campagne (CA, tonnage, rendement, charges)"
                              disabled={Boolean(enCours)}
                              onClick={() => deepScan(d)}
                              className={`inline-flex items-center gap-1 border px-1.5 py-1 text-[10px] font-bold uppercase disabled:opacity-50 ${
                                d.analyseProfonde
                                  ? 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-700 hover:text-white'
                                  : 'border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-700 hover:text-white'
                              }`}
                            >
                              <BarChart3 size={12} /> {d.analyseProfonde ? 'Relancer' : 'Deep Scan'}
                            </button>
                          )}
                          {onglet === 'corbeille' ? (
                            <button type="button" data-testid={`restaurer-${d.id}`} title="Restaurer" onClick={async () => { const { error } = await restaurerDocument(d.id); if (error) return toast.error(error); toast.success('Document restauré'); load(); }} className="border border-slate-300 p-1.5 text-slate-600 hover:bg-emerald-700 hover:text-white">
                              <RotateCcw size={13} />
                            </button>
                          ) : (
                            <button type="button" data-testid={`corbeille-${d.id}`} title="Mettre à la corbeille (30 jours)" onClick={async () => { const { error, purgeLe } = await mettreALaCorbeille(d.id, user?.name); if (error) return toast.error(error); toast.success(`À la corbeille — purge définitive le ${purgeLe}`); load(); }} className="border border-slate-300 p-1.5 text-slate-500 hover:bg-red-600 hover:text-white">
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {kpis.length > 0 && (
        <section data-testid="section-kpis" className="border border-slate-200 border-l-2 border-l-amber-600 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <p className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
              <BarChart3 size={16} className="text-amber-700" /> Historique & comparatifs de campagnes — KPI extraits par l’IA
            </p>
            <p className="text-xs text-slate-500">
              Ces chiffres alimentent le comparatif N vs N-1 du tableau de bord. Corrigez-les si la lecture IA s’est trompée.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="table-kpis">
              <thead className="bg-slate-50">
                <tr>{['Campagne', 'CA (TND)', 'Olives (kg)', 'Huile (kg)', 'Rdt (%)', 'Achats olives', 'Charges', 'Résultat net', 'Document', ''].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {kpis.map((k) => (
                  <tr key={k.id} data-testid={`kpi-campagne-${k.id}`}>
                    {[
                      ['campagne', 'text', 'w-24'],
                      ['chiffreAffaires', 'number', 'w-28'],
                      ['volumeOlivesKg', 'number', 'w-28'],
                      ['volumeHuileKg', 'number', 'w-28'],
                      ['rendementMoyen', 'number', 'w-20'],
                      ['achatsOlivesTnd', 'number', 'w-28'],
                      ['chargesTotal', 'number', 'w-28'],
                      ['resultatNet', 'number', 'w-28'],
                    ].map(([champ, type, largeur]) => (
                      <td key={champ} className={td}>
                        <input
                          data-testid={`kpi-${champ}-${k.id}`}
                          type={type}
                          step="0.001"
                          defaultValue={k[champ]}
                          onBlur={async (e) => {
                            const valeur = type === 'number' ? Number(e.target.value) || 0 : e.target.value.trim();
                            if (String(valeur) === String(k[champ])) return;
                            const { error } = await majKpiCampagne(k.id, { [champ]: valeur });
                            if (error) return toast.error(error);
                            toast.success('Indicateur corrigé');
                            load();
                          }}
                          className={`h-9 ${largeur} border border-slate-200 bg-slate-50 px-1.5 text-right font-data text-xs outline-none focus:border-slate-900 focus:bg-white`}
                        />
                      </td>
                    ))}
                    <td className={`${td} text-xs text-slate-600`}>
                      {k.documentNom}
                      <span className="block font-data text-[10px] text-slate-400">
                        {(k.maj || '').slice(0, 16).replace('T', ' ')}
                        {k.corrigeManuellement ? ' · corrigé à la main' : ' · lecture IA'}
                      </span>
                    </td>
                    <td className={`${td} text-right`}>
                      <button
                        type="button"
                        data-testid={`kpi-supprimer-${k.id}`}
                        onClick={async () => {
                          const { error } = await supprimerKpiCampagne(k.id);
                          if (error) return toast.error(error);
                          toast.success('Fiche KPI supprimée');
                          load();
                        }}
                        className="border border-slate-300 p-1.5 text-slate-500 hover:bg-red-600 hover:text-white"
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {lot && (
        <div data-testid="modale-lot" className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-5 py-4">
              <p className="flex items-center gap-2 font-heading text-lg font-black text-slate-900">
                <Scissors size={18} className="text-violet-700" /> Découpage intelligent — {lot.segments.length} document(s) détecté(s)
              </p>
              <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">{lot.file.name} · {lot.pages} pages</p>
            </div>
            <table className="w-full border-collapse">
              <thead className="bg-slate-50">
                <tr>{['Pages', 'Catégorie', 'Type', 'Tiers', 'Date', 'Montant', 'Résumé'].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lot.segments.map((s, i) => (
                  <tr key={i} data-testid={`segment-${i}`}>
                    <td className={`${td} font-data text-xs font-bold`}>{s.pageDebut === s.pageFin ? s.pageDebut : `${s.pageDebut}–${s.pageFin}`}</td>
                    <td className={`${td} text-xs`}>{s.categorie}</td>
                    <td className={`${td} text-xs`}>{s.typeDocument || '—'}</td>
                    <td className={`${td} text-xs`}>{s.tiers || '—'}</td>
                    <td className={`${td} font-data text-xs`}>{s.date || '—'}</td>
                    <td className={`${td} tabular text-right font-data text-xs`}>{s.montant ? fmt(s.montant, 3) : '—'}</td>
                    <td className={`${td} text-xs text-slate-500`}>{s.resume || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-wrap gap-2 border-t border-slate-200 px-5 py-4">
              <button type="button" data-testid="lot-annuler" onClick={() => setLot(null)} className="h-11 flex-1 border border-slate-300 text-sm font-bold text-slate-700 hover:bg-slate-100">
                Annuler
              </button>
              <button type="button" data-testid="lot-appliquer" disabled={Boolean(enCours)} onClick={appliquerLot} className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold text-white hover:bg-slate-700 disabled:opacity-50">
                Archiver les {lot.segments.length} documents séparément
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
